import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import { message, Spin } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { academyPaperApi } from '@/services/api';
import axios from 'axios';

const PaperPaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slugPrefix = getSlugPrefix(pathname);
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const confirmRequestedRef = useRef(false);

  // URL 파라미터
  const paymentKey = searchParams.get('paymentKey');
  const orderId = searchParams.get('orderId');
  const amount = searchParams.get('amount');
  const paperId = searchParams.get('paperId');
  const quantity = searchParams.get('quantity');
  const academyId = searchParams.get('academyId');
  const academyCode = searchParams.get('academyCode');

  useEffect(() => {
    if (!paymentKey || !orderId || !amount || !paperId || !quantity || (!academyId && !academyCode)) {
      message.error('결제 정보가 올바르지 않습니다.');
      navigate(`${slugPrefix}/academy/paper/list`);
      return;
    }

    if (confirmRequestedRef.current) {
      return;
    }
    confirmRequestedRef.current = true;

    // 토큰 유효성 확인 및 리프레시
    const ensureValidToken = async () => {
      const token = localStorage.getItem('academy_token');
      if (!token) {
        return false;
      }

      try {
        // 토큰 만료 확인
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        const expSec = payload?.exp ? Number(payload.exp) : 0;
        const nowSec = Math.floor(Date.now() / 1000);
        
        // 토큰이 만료되었거나 만료 임박(60초 이내)인 경우 리프레시 시도
        if (expSec && expSec - nowSec <= 60) {
          const refreshToken = localStorage.getItem('academy_refresh_token');
          if (refreshToken) {
            try {
              const isDev = process.env.NODE_ENV === 'development';
              const API_BASE_URL = isDev ? '/api' : (process.env.REACT_APP_API_BASE_URL || '/api');
              
              const res = await axios.post(
                `${API_BASE_URL}/auth/refresh`,
                { refreshToken },
                { headers: { 'Content-Type': 'application/json' } }
              );
              
              const newToken = res?.data?.data?.token || res?.data?.token;
              if (newToken) {
                localStorage.setItem('academy_token', newToken);
                console.log('✅ 토큰 리프레시 성공');
                return true;
              }
            } catch (refreshError) {
              console.error('토큰 리프레시 실패:', refreshError);
              return false;
            }
          }
          return false;
        }
        
        return true;
      } catch (error) {
        console.error('토큰 검증 실패:', error);
        return false;
      }
    };

    // 결제 승인 요청
    const confirmPayment = async () => {
      // 먼저 토큰 유효성 확인
      const isTokenValid = await ensureValidToken();
      if (!isTokenValid) {
        setErrorMessage('세션이 만료되었습니다. 다시 로그인 후 결제 내역을 확인해주세요.');
        setIsProcessing(false);
        return;
      }

      try {
        const payload: any = {
          paymentKey,
          orderId,
          amount: Number(amount),
          paperId: Number(paperId),
          quantity: Number(quantity),
        };
        if (academyId) payload.academyId = Number(academyId);
        if (academyCode) payload.academyCode = academyCode;
        const result = await academyPaperApi.confirmPayment(payload);

        if (result.success) {
          setPaymentInfo(result.data);
          message.success('결제가 완료되었습니다!');
        } else {
          throw new Error(result.message || '결제 승인 실패');
        }
      } catch (error: any) {
        console.error('결제 승인 실패:', error);
        const errorMsg = error?.response?.data?.message || error?.message || '결제 승인에 실패했습니다.';
        setErrorMessage(errorMsg);
        
        // 인증 에러가 아닌 경우에만 에러 메시지 표시
        if (error?.response?.status !== 401 && error?.response?.status !== 403) {
          message.error(errorMsg);
        } else {
          // 인증 에러인 경우 사용자에게 로그인 필요 안내
          setErrorMessage('세션이 만료되었습니다. 다시 로그인 후 결제 내역을 확인해주세요.');
        }
      } finally {
        setIsProcessing(false);
      }
    };

    confirmPayment();
  }, [paymentKey, orderId, amount, paperId, quantity, academyId, academyCode, navigate]);

  if (isProcessing) {
    return (
      <div style={{ padding: '80px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '24px', fontSize: '16px' }}>결제 승인 처리 중...</div>
      </div>
    );
  }

  // 에러가 발생한 경우
  if (errorMessage) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '24px' }}>⚠️</div>
        <h2 style={{ marginBottom: '16px', color: '#ff4d4f' }}>결제 처리 중 오류가 발생했습니다</h2>
        
        <div style={{ padding: '24px', background: '#fff2f0', borderRadius: '8px', marginBottom: '32px', border: '1px solid #ffccc7' }}>
          <p style={{ color: '#cf1322', marginBottom: '16px', fontSize: '14px' }}>{errorMessage}</p>
          <p style={{ fontSize: '14px', color: '#595959' }}>
            결제는 정상적으로 완료되었을 수 있습니다.<br />
            마이페이지에서 구매 내역을 확인해주세요.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={() => navigate(`${slugPrefix}/academy/login`)}
            style={{
              padding: '12px 24px',
              background: '#fff',
              color: '#1890ff',
              border: '1px solid #1890ff',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            로그인
          </button>
          <button
            onClick={() => navigate(`${slugPrefix}/academy/mypage/paper`)}
            style={{
              padding: '12px 24px',
              background: '#1890ff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            구매 내역 확인
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '80px auto', padding: '40px', textAlign: 'center' }}>
      <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '24px' }} />
      <h2 style={{ marginBottom: '16px' }}>결제가 완료되었습니다!</h2>
      
      {paymentInfo && (
        <div style={{ padding: '24px', background: '#f5f5f5', borderRadius: '8px', marginBottom: '32px', textAlign: 'left' }}>
          <div style={{ marginBottom: '12px' }}>
            <strong>주문번호:</strong> {paymentInfo.orderId || orderId}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>시험지:</strong> {paymentInfo.paperTitle}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>구매 수량:</strong> {quantity}개
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>결제 금액:</strong> {Number(amount).toLocaleString()}원
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>결제 방법:</strong> {paymentInfo.method || '카드'}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button
          onClick={() => navigate(`${slugPrefix}/academy/mypage/paper`)}
          style={{
            padding: '12px 24px',
            background: '#fff',
            color: '#1890ff',
            border: '1px solid #1890ff',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          구매 내역 확인
        </button>
        <button
          onClick={() => navigate(`${slugPrefix}/academy/paper/list`)}
          style={{
            padding: '12px 24px',
            background: '#1890ff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          시험지 목록으로
        </button>
      </div>
    </div>
  );
};

export default PaperPaymentSuccessPage;

